import { useState } from 'react';
import { SelectOption } from '../../searchableSelect/searchableSelectTypes';
import { useLanguage } from '../../../context/LanguageContext';
import { useTransaction } from '../../../context/TransactionContext';
import { useFetchMatters } from '../../../hooks/useFetchMatters';
import { useOwnerSelectFilter } from '../../../hooks/useOwnerSelectFilter';
import { useFetchTransactionRestrictions } from '../../../hooks/useFetchTransactionRestrictions';
import { useFetchTransactionTypes } from '../../../hooks/useFetchTransactionTypes';
import { useHandleTransactionRestrictions } from '../../../hooks/useHandleTransactionRestrictions';
import { IMatter } from '../../../interface/matter';
import {
  buildMatterSelection,
  DEFAULT_TRANSACTION_TYPE_FIELDS,
  getPrincipalName,
  MATTER_SELECTION_PAGE_SIZE,
  MATTER_SELECTION_QUERY,
  toMatterOption,
} from './matterSelection';

interface UseMatterSelectProps {
  transactionTypeInUse?: boolean;
}

/**
 * The props `SearchableSelect` needs to pick a matter, and the restriction dialogs that picking one
 * can raise. The matter is applied only after the same checks the picker page makes.
 */
export const useMatterSelect = ({ transactionTypeInUse }: UseMatterSelectProps) => {
  const { transaction, updateTransaction } = useTransaction();
  const { getAppLanguageCode } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const { isMyOwn, filters } = useOwnerSelectFilter();
  const [selectedMatter, setSelectedMatter] = useState<IMatter | undefined>(undefined);
  const {
    data: matters,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchMatters({
    ...MATTER_SELECTION_QUERY,
    sortBy: ['openingDate:DESC'],
    pageSize: MATTER_SELECTION_PAGE_SIZE,
    search: searchValue,
    myOwn: isMyOwn,
  });
  const { data: defaultTransactionType, isLoading: isDefaultTransactionTypeLoading } =
    useFetchTransactionTypes({
      defaultTransactionTypeId: selectedMatter?.defaultTransactionTypeId,
      fields: DEFAULT_TRANSACTION_TYPE_FIELDS,
    });
  const { data: matterTransactionRestrictions, isLoading: isCheckingRestrictions } =
    useFetchTransactionRestrictions({
      matterId: selectedMatter?.id ?? '',
      enabled: selectedMatter?.id !== undefined && !isDefaultTransactionTypeLoading,
    });

  const applyMatter = (matter: IMatter) => {
    updateTransaction(
      buildMatterSelection(transaction, matter, {
        defaultTransactionType,
        transactionTypeInUse,
        appLanguage: getAppLanguageCode(),
      }),
    );
    setSelectedMatter(undefined);
  };
  const {
    customerIdentificationErrorDialog,
    setCustomerIdentificationErrorDialog,
    serviceRestrictionErrorDialog,
    setServiceRestrictionErrorDialog,
  } = useHandleTransactionRestrictions({
    selectedMatter,
    matterTransactionRestrictions,
    onSelect: applyMatter,
  });

  const options: SelectOption[] = matters.map((matter: IMatter) =>
    toMatterOption(matter, getPrincipalName(matter)),
  );
  const value: SelectOption | null = transaction?.matter?.id
    ? toMatterOption(transaction.matter, getPrincipalName(transaction))
    : null;

  const onChange = (option: SelectOption) => {
    if (option.value === transaction?.matter?.id) return;
    const matter = matters.find(({ id }: IMatter) => id === option.value);
    if (!matter) return;
    setSelectedMatter(matter);
  };

  return {
    select: {
      options,
      value,
      onChange,
      onSearch: setSearchValue,
      onLoadMore: fetchNextPage,
      hasMore: Boolean(hasNextPage),
      loading: isLoading,
      loadingMore: isFetchingNextPage,
      filters,
    },
    dialogs: {
      customerIdentificationErrorDialog,
      setCustomerIdentificationErrorDialog,
      serviceRestrictionErrorDialog,
      setServiceRestrictionErrorDialog,
      onDismiss: () => setSelectedMatter(undefined),
    },
    isCheckingRestrictions,
  };
};
